import React, { useState } from 'react';
import type { SourceReference } from '../../types/chat.types';
import { Icon } from '../common/Icon';
import copySvg from '../../assets/copy.svg';
import dropdownSvg from '../../assets/dropdown.svg';

interface CitationCardProps {
  source: SourceReference;
  index: number;
}

export const CitationCard: React.FC<CitationCardProps> = ({ source, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Determine if this is a discourse source
  const isDiscourse = source.metadata.source_folder === 'discourses';

  // Get display values for discourse vs other sources
  const sourceTypeDisplay = isDiscourse ? 'Discourse' : source.source_type;
  const titleDisplay = source.metadata.discourse_title || source.title;

  // Extract readable chunk identifier
  const getChunkDisplay = (): string => {
    if (isDiscourse && source.metadata.discourse_id) {
      // Extract chunk number from chunk_id (e.g., "Simple_chunk_1" → "Chunk 1")
      const chunkMatch = source.chunk_id.match(/_chunk_(\d+)$/);
      return chunkMatch ? `Chunk ${chunkMatch[1]}` : source.metadata.chunk_position || '';
    }
    return source.metadata.chunk_position || '';
  };

  const handleCopy = () => {
    const text = `[${index}] ${sourceTypeDisplay} | ${titleDisplay}

${source.content_full}

${source.metadata.topics ? `Topics: ${source.metadata.topics.join(', ')}` : ''}
${source.metadata.page_number ? `Page: ${source.metadata.page_number}` : ''}`;

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="border rounded-lg p-2 md:p-3 mb-2 bg-gray-50 dark:bg-gray-800 dark:border-gray-700">
      <div className="flex flex-col md:flex-row md:justify-between md:items-start">
        <div className="flex-1 w-full md:w-auto">
          {/* Header */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-blue-600 dark:text-blue-400">[{index}]</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">{sourceTypeDisplay}</span>
            <span className="text-sm text-gray-400">|</span>
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">{titleDisplay}</span>
            {getChunkDisplay() && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                ({getChunkDisplay()})
              </span>
            )}
            {source.metadata.page_number && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                • p. {source.metadata.page_number}
              </span>
            )}
          </div>

          {/* Content */}
          <div className="text-sm text-gray-700 dark:text-gray-300 citation-card-content">
            {isExpanded ? (
              <div>
                <p className="whitespace-pre-wrap mb-2">{source.content_full}</p>

                {/* Metadata Section */}
                <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                  {source.metadata.topics && source.metadata.topics.length > 0 && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <span className="font-semibold">Topics:</span> {source.metadata.topics.join(', ')}
                    </div>
                  )}
                  {source.metadata.key_terms && source.metadata.key_terms.length > 0 && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
                      <span className="font-semibold">Key Terms:</span> {source.metadata.key_terms.join(', ')}
                    </div>
                  )}
                  {source.metadata.emphasis_markers && source.metadata.emphasis_markers.length > 0 && (
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <span className="font-semibold">Emphasis:</span> {source.metadata.emphasis_markers.join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-gray-600 dark:text-gray-400">{source.preview_short}</p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-2 md:mt-0 md:ml-3 flex-shrink-0 justify-end md:justify-start">
          {isExpanded && (
            <button
              onClick={handleCopy}
              className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 dark:bg-blue-900 dark:hover:bg-blue-800 rounded transition-colors flex items-center justify-center"
              title="Copy citation"
              aria-label="Copy citation text"
            >
              {copied ? '✓' : <Icon src={copySvg} alt="Copy" size={14} />}
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs px-3 py-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded transition-all flex items-center justify-center"
            aria-label={isExpanded ? 'Collapse citation' : 'Expand citation'}
          >
            <Icon
              src={dropdownSvg}
              alt={isExpanded ? 'Collapse' : 'Expand'}
              size={12}
              className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};
